import React, { useEffect, useState, useRef } from "react";
import { Col, Container, Row, Form, Button, Spinner, Table } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import Pagination from "../Common/Pagination";
import { useSelector } from "react-redux";

const NewPropertyFound = () => {
  const navigate = useNavigate();
  const financialYear = useSelector((state) => state.activeData.financialYear);

  // ---------------- FILTER STATES ----------------
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedGat, setSelectedGat] = useState("");
  const [searchProperty, setSearchProperty] = useState("");

  // ---------------- DATA STATES ----------------
  const [reportData, setReportData] = useState(null);
  const [loader, setLoader] = useState(true);

  // ---------------- ZONE + GAT STATES ----------------
  const [zoneListFil, setZoneListFil] = useState([]);
  const [filterdGatListFil, setFilterdGatListFil] = useState([]);

  // ---------------- PAGINATION ----------------
  const [pageNo, setPageNo] = useState(1);
  const [docPerPage, setDocPerPage] = useState(50);
  const [noOfPages, setNoOfPages] = useState(0);

  const requestRef = useRef(0);
  const prevYearRef = useRef(financialYear);

  useEffect(() => {
    const isYearChanged = prevYearRef.current !== financialYear;

    if (isYearChanged) {
      if (financialYear !== "2026-2027") {
        navigate("/dashboard/new-home");
        return;
      } else {
        setSelectedZone("");
        setSelectedGat("");
        setSearchProperty("");
        setPageNo(1);
        setReportData();
        prevYearRef.current = financialYear;
      }
    }

    getNewPropertyReport(
      isYearChanged ? 1 : pageNo,
      isYearChanged ? "" : selectedZone,
      isYearChanged ? "" : selectedGat,
      isYearChanged ? "" : searchProperty
    );
  }, [pageNo, docPerPage, financialYear]);


  // ---------------- FETCH ZONES ----------------
  const getZoneList = async () => {
    try {
      const res = await fetch(
        process.env.REACT_APP_BASE_URL + "/zoneData/getZoneData"
      );
      const response = await res.json();
      setZoneListFil(response.data || []);
    } catch (err) {
      console.log("Error fetching zones", err);
    }
  };

  // ---------------- FETCH GATS (LIKE MANAGEROLES) ----------------
  const getGatData = async (zoneKey) => {
    try {
      const res = await fetch(
        process.env.REACT_APP_BASE_URL + "/gatData/getGatData",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ zonekey: zoneKey }),
        }
      );

      const response = await res.json();
      setFilterdGatListFil(response.data || []);
    } catch (err) {
      console.log("Error fetching gats", err);
    }
  };

  // ---------------- ZONE CHANGE EFFECT ----------------
  useEffect(() => {
    if (selectedZone) {
      getGatData(selectedZone);
    } else {
      setFilterdGatListFil([]);
    }

    setSelectedGat("");
  }, [selectedZone]);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    getZoneList();
  }, []);

  // ---------------- FETCH REPORT ----------------
  const getNewPropertyReport = async (
    overridePage = pageNo,
    sZone = selectedZone,
    sGat = selectedGat,
    pCode = searchProperty
  ) => {
    const requestId = ++requestRef.current;
    setLoader(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_BASE_URL + "/newPropertyFound/readPgn",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            zone_id: sZone || "",
            gat_id: sGat || "",
            referencePropertyCode: pCode || "",
            documentsPerPage: docPerPage,
            page: overridePage,
            financialYear: financialYear,
          }),
        }
      );

      const result = await response.json();

      if (requestId !== requestRef.current) return;

      if (result.status === 200) {
        setReportData(result);
        setNoOfPages(result.noOfPages || 0);
      }
    } catch (error) {
      if (requestId === requestRef.current) {
        setReportData();
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoader(false);
      }
    }
  };


  const handleSearch = () => {
    setPageNo(1);
    getNewPropertyReport(1);
  };

  return (
    <div className="manageRoles mt-1">
      <Container>

        {/* ---------------- FILTER SECTION ---------------- */}
        <Row className="d-flex justify-content-center gap-3">

          <Col md={6} className="searchCard">
            <div className="label mb-2 mx-auto">
              <p>Search by Zone/Gat</p>
            </div>

            <Row className="parentRow d-flex justify-content-center">

              {/* ZONE */}
              <Col md={5}>
                <Form.Select
                  value={selectedZone}
                  onChange={(e) => setSelectedZone(e.target.value)}
                >
                  <option value="">Select Zone</option>
                  {zoneListFil.map((z) => (
                    <option key={z.zonekey} value={z.zonekey}>
                      {z.zonename + "-" + z.zoneno}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* GAT */}
              <Col md={4}>
                <Form.Select
                  value={selectedGat}
                  onChange={(e) => setSelectedGat(e.target.value)}
                  disabled={!selectedZone}
                >
                  <option value="">Select Gat</option>
                  {filterdGatListFil.map((g) => (
                    <option key={g.gatkey} value={g.gatkey}>
                      {g.mar_gatname + "-" + g.gatname}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* SEARCH */}
              <Col md={2}>
                <Button className="btn btn-submit" onClick={handleSearch}>
                  <Icon.Search size={14} className="me-2" />
                  Search
                </Button>
              </Col>

            </Row>
          </Col>

          {/* PROPERTY SEARCH */}
          <Col md={4} className="searchCard">
            <div className="label mb-2 mx-auto">
              <p>Search by Property Code</p>
            </div>

            <Row>
              <Col md={8}>
                <Form.Control
                  placeholder="Enter Property Code"
                  value={searchProperty}
                  onChange={(e) => {
                    if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setSearchProperty(e.target.value);
                    }
                  }}
                />
              </Col>
              <Col md={4}>
                <Button className="btn btn-submit w-100" onClick={handleSearch}>
                  <Icon.Search size={14} className="me-2" />
                  Search
                </Button>
              </Col>
            </Row>
          </Col>

        </Row>

        {/* ---------------- TABLE ---------------- */}
        <Row className="mt-4">
          <div className="d-flex align-items-center mb-2">
            <Icon.Table size={24} className="me-2" />
            <h5 className="mb-0">New Property Found</h5>
          </div>

          <div className="tbl-container p-0">
            <div className="tbl-fixed" style={{ maxHeight: "73vh" }}>
              <Table responsive hover>
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th>Sr. No.</th>
                    <th>Zone</th>
                    <th>Gat</th>
                    <th>Property Code</th>
                    <th>Property Type</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <Spinner />
                      </td>
                    </tr>
                  ) : reportData?.data?.length > 0 ? (
                    reportData.data.map((row, index) => (
                      <tr key={row._id} style={{ textAlign: "center" }}>
                        <td>
                          {pageNo !== 1
                            ? index + 1 + docPerPage * (pageNo - 1)
                            : index + 1}
                        </td>
                        <td>{row.zonename + "-" + row.zoneno}</td>
                        <td>{row.gatname}</td>
                        <td>{row.referencePropertyCode}</td>
                        <td>{row.propertyType}</td>
                        <td>
                          <Button
                            variant="link"
                            onClick={() =>
                              navigate("/dashboard/new-property-view", {
                                state: { report: row },
                              })
                            }
                          >
                            <Icon.Eye size={20} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* PAGINATION */}
            <Row>
              <Col md={4}>
                <h6>
                  Total - <strong>{reportData?.count || 0}</strong>
                </h6>
              </Col>

              <Col md={8} className="d-flex justify-content-end">
                <Pagination
                  currentPage={pageNo}
                  totalPages={noOfPages}
                  onPageChange={setPageNo}
                  onDocsPerPage={setDocPerPage}
                  docsPerPage={docPerPage}
                />
              </Col>
            </Row>

          </div>
        </Row>

      </Container>
    </div>
  );
};

export default NewPropertyFound;